import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from './AuthContext'
import { isModuleEnabled as moduleEnabled, visibleTabs } from '../config/modules'

const CampaignContext = createContext(null)

// Exported for unit testing without React.
//
// The server is authoritative on canEdit — it already accounts for a site admin
// being an implicit DM everywhere. We deliberately do NOT recompute it from
// membershipRole, because a site admin who is also a plain player member has
// membershipRole 'player' and canEdit true.
export function deriveCanEdit(campaign, viewAsPlayer) {
  if (!campaign) return false
  return Boolean(campaign.canEdit) && !viewAsPlayer
}

export function CampaignProvider({ children }) {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { user, viewAsPlayer, authEpoch } = useAuth()

  const [campaigns, setCampaigns] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // authEpoch: see the identical comment in tabs/ItemsTab.jsx — this is the
  // one that matters most, since every tab downstream depends on `campaign`
  // being non-null; if the very first /api/campaigns fetch is what 401'd,
  // nothing else recovers until this does.
  const loadCampaigns = useCallback(async () => {
    if (!user) return
    try {
      const res = await fetch('/api/campaigns')
      if (!res.ok) throw new Error('Failed to load campaigns')
      setCampaigns(await res.json())
      setError(null)
    } catch (err) {
      setError(err.message)
      setCampaigns([])
    } finally {
      setLoading(false)
    }
  }, [user, authEpoch])

  useEffect(() => { loadCampaigns() }, [loadCampaigns])

  const campaign = useMemo(
    () => campaigns.find((c) => c.slug === slug) || null,
    [campaigns, slug]
  )

  // An unknown slug, or one the user isn't a member of, sends them back to the
  // picker rather than showing a broken shell.
  useEffect(() => {
    if (!loading && slug && !campaign) {
      navigate('/campaigns', { replace: true })
    }
  }, [loading, slug, campaign, navigate])

  const canEdit = deriveCanEdit(campaign, viewAsPlayer)
  const realCanEdit = deriveCanEdit(campaign, false)

  const value = useMemo(() => ({
    campaign,
    campaigns,
    loading,
    error,
    membershipRole: campaign?.membershipRole ?? null,
    canEdit,
    realCanEdit,
    isModuleEnabled: (id) => moduleEnabled(campaign?.enabledModules, id),
    tabs: visibleTabs(campaign?.enabledModules),
    refreshCampaigns: loadCampaigns,
  }), [campaign, campaigns, loading, error, canEdit, realCanEdit, loadCampaigns])

  return <CampaignContext.Provider value={value}>{children}</CampaignContext.Provider>
}

// Inert defaults outside a provider, so components that render on non-campaign
// routes (e.g. the picker) don't have to guard every call.
const INERT = {
  campaign: null,
  campaigns: [],
  loading: false,
  error: null,
  membershipRole: null,
  canEdit: false,
  realCanEdit: false,
  isModuleEnabled: () => false,
  tabs: [],
  refreshCampaigns: () => {},
}

export function useCampaign() {
  return useContext(CampaignContext) || INERT
}
